import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

/**
 * FieldRow — module-scoped input component for SHND forms.
 *
 * IMPORTANT: This MUST live at module scope (not inside a parent component)
 * to avoid losing input focus on each keystroke caused by remounting.
 *
 * Built-in validation:
 *   - validation="email"   → must be a valid email
 *   - validation="mobile"  → 10–15 digits; auto-strips non-digits
 *   - validation="age"     → 0–120 integer; auto-strips non-digits
 *   - validation="its"     → exactly 8 digits; auto-strips non-digits
 *   - validation="number"  → numeric (decimal allowed); auto-strips non-numeric
 */
export default function FieldRow({
  label,
  value,
  onChange,
  type = 'text',
  placeholder = '',
  validation = null,
  required = false,
  maxLength,
  testId,
}) {
  const v = value == null ? '' : String(value);

  const sanitize = (raw) => {
    if (validation === 'mobile' || validation === 'age' || validation === 'its') {
      return raw.replace(/\D/g, '');
    }
    if (validation === 'number') {
      // allow digits + single decimal point
      const cleaned = raw.replace(/[^\d.]/g, '');
      const firstDot = cleaned.indexOf('.');
      return firstDot === -1
        ? cleaned
        : cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
    }
    return raw;
  };

  const validate = (raw) => {
    if (!raw) return required ? 'This field is required' : '';
    switch (validation) {
      case 'email': {
        const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());
        return ok ? '' : 'Enter a valid email address';
      }
      case 'mobile': {
        const digits = raw.replace(/\D/g, '');
        if (digits.length < 10) return 'Mobile must be at least 10 digits';
        if (digits.length > 15) return 'Mobile cannot be more than 15 digits';
        return '';
      }
      case 'age': {
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0 || n > 120) return 'Age must be between 0 and 120';
        return '';
      }
      case 'its': {
        return /^\d{8}$/.test(raw) ? '' : 'ITS must be exactly 8 digits';
      }
      default:
        return '';
    }
  };

  // Compute effective input attributes from validation type
  let effectiveType = type;
  let inputMode;
  let computedMaxLength = maxLength;
  if (validation === 'email') effectiveType = 'email';
  if (validation === 'mobile') {
    effectiveType = 'tel';
    inputMode = 'numeric';
    if (!computedMaxLength) computedMaxLength = 15;
  }
  if (validation === 'its') {
    inputMode = 'numeric';
    computedMaxLength = 8;
  }
  if (validation === 'age') {
    inputMode = 'numeric';
    if (!computedMaxLength) computedMaxLength = 3;
  }
  if (validation === 'number') {
    inputMode = 'decimal';
  }

  const error = validate(v);
  const showError = !!error && !!v; // don't show "required" error until user types

  const handleChange = (raw) => {
    const sanitized = sanitize(raw);
    onChange(sanitized);
  };

  return (
    <div className="space-y-1">
      <Label className="text-sm font-medium text-slate-700">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {type === 'textarea' ? (
        <Textarea
          value={v}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          className="text-sm"
          maxLength={computedMaxLength}
          data-testid={testId}
        />
      ) : (
        <Input
          type={effectiveType}
          inputMode={inputMode}
          value={v}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          className={`text-sm ${showError ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
          maxLength={computedMaxLength}
          data-testid={testId}
        />
      )}
      {showError && (
        <p className="text-xs text-red-500" data-testid={testId ? `${testId}-error` : undefined}>{error}</p>
      )}
    </div>
  );
}
